import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { isRouteErrorResponse, Outlet, useRouteError } from "react-router";
import {
  CHUNK_RELOAD_SESSION_KEY,
  isChunkLoadError,
  reloadOnceForStaleChunks,
} from "@/utils/chunkLoadError";
import { recordPulseClientError } from "@/services/api/user";
import { ChunkLoadErrorFallback } from "./ChunkLoadErrorFallback";
import NotFound from "./NotFound";

function captureRouteError(error: unknown, chunkLoad: boolean): string | undefined {
  let eventId: string | undefined;
  Sentry.withScope((scope) => {
    scope.setTag("chunk_load", chunkLoad ? "true" : "false");
    const toCapture =
      error instanceof Error
        ? error
        : new Error(
            typeof error === "string"
              ? error
              : "Route error (non-Error thrown)",
          );
    eventId = Sentry.captureException(toCapture);
  });
  return eventId;
}

export default function ErrorBoundary() {
  const error = useRouteError();
  const chunkLoad = !isRouteErrorResponse(error) && isChunkLoadError(error);

  const [awaitingChunkReload, setAwaitingChunkReload] = useState(() => {
    if (!chunkLoad || typeof sessionStorage === "undefined") return false;
    try {
      return sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) !== "1";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (error == null) {
      return;
    }

    if (isRouteErrorResponse(error) && error.status === 404) {
      return;
    }

    if (chunkLoad) {
      if (reloadOnceForStaleChunks({ source: "route-error-boundary" })) {
        setAwaitingChunkReload(true);
        return;
      }
      const sentryEventId = captureRouteError(error, true);
      recordPulseClientError({
        route: window.location.pathname,
        error_class: "ChunkLoadError",
        sentry_event_id: sentryEventId,
      }).catch(() => {});
      return;
    }

    const sentryEventId = captureRouteError(error, false);
    recordPulseClientError({
      route: window.location.pathname,
      error_class: error instanceof Error ? error.name : "RouteError",
      sentry_event_id: sentryEventId,
    }).catch(() => {});
  }, [error, chunkLoad]);

  // After a route error clears, React Router can remount this boundary with no payload.
  // Rendering null or an error shell here leaves a blank page on /settings refresh.
  if (error == null) {
    return <Outlet />;
  }

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />;
  }

  if (awaitingChunkReload) {
    return (
      <main
        className="pt-16 p-4 container mx-auto"
        data-testid="chunk-load-reloading"
      >
        <p className="text-gray-700 dark:text-gray-300">Loading update...</p>
      </main>
    );
  }

  if (chunkLoad) {
    return <ChunkLoadErrorFallback variant="page" />;
  }

  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = "Error";
    details = error.statusText || details;
  } else if (import.meta.env.DEV && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto" data-testid="route-error">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
        {message}
      </h1>
      <p className="mt-2 text-gray-700 dark:text-gray-300">{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto mt-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <code className="text-sm text-gray-800 dark:text-gray-200">
            {stack}
          </code>
        </pre>
      )}
    </main>
  );
}
